import { Card, LiveDot, ProvenanceTag, Sparkline } from "@/ui";
import { realtimeBase, useLiveEvents, useLiveVisitors } from "@/mock";
import type { Point } from "@/mock";
import { useWorkspace } from "@/app/WorkspaceContext";
import { useRealtime } from "@/data/hooks";
import { PanelHeader } from "../parts";
import { AigenTag, EmptyState, SampleChip, Skeleton, panelState, relativeTime } from "../wiring";

/** Human label for a raw realtime event type. */
function verb(type: string): string {
  switch (type) {
    case "pageview":
    case "$pageview":
      return "viewed";
    case "click":
    case "$autocapture":
      return "clicked on";
    case "scroll":
      return "scrolled";
    case "form_submit":
    case "submit":
      return "submitted on";
    default:
      return `triggered ${type} on`;
  }
}

/** Sample (mock) realtime view — drifts a simulated baseline. */
function SampleRealtime() {
  const { count, history } = useLiveVisitors();
  const events = useLiveEvents(8);
  const spark: Point[] = history.map((v, i) => ({ t: String(i), v }));

  return (
    <div className="grid gap-4 lg:grid-cols-5">
      <Card className="flex flex-col justify-between p-5 lg:col-span-2">
        <div className="flex items-center justify-between">
          <LiveDot label="Live visitors" />
          <span className="flex items-center gap-2">
            <ProvenanceTag source="posthog" />
            <SampleChip />
          </span>
        </div>
        <div className="mt-4">
          <div className="font-semibold leading-none tracking-tight text-accent text-[64px]">
            {count}
          </div>
          <p className="mt-2 text-[13px] text-muted">on the site right now</p>
        </div>
        <div className="mt-4">
          <Sparkline data={spark} color="accent" height={40} fill />
          <p className="mt-1 font-mono text-[10px] text-subtle">last ~50s · sample</p>
        </div>
      </Card>

      <Card className="p-5 lg:col-span-3">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-[14px] font-semibold tracking-tight text-ink-strong">
            Activity stream
          </h3>
          <SampleChip />
        </div>
        <ul className="space-y-2.5">
          {events.map((e) => (
            <li
              key={e.id}
              className="flex items-center gap-3 border-b border-hairline/60 pb-2.5 last:border-0 last:pb-0"
            >
              <span className="relative flex h-1.5 w-1.5 shrink-0">
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-accent" />
              </span>
              <p className="min-w-0 flex-1 truncate text-[13px] text-ink">
                Someone <span className="text-muted">{e.label}</span>{" "}
                <span className="font-mono text-[12px] text-ink-strong">{e.page}</span>
              </p>
              <span className="shrink-0 text-[12px] text-subtle">{e.city}</span>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}

export function Realtime() {
  const { siteId, live } = useWorkspace();
  const rtQ = useRealtime(siteId);

  const state = panelState({
    siteId,
    workspaceLive: live,
    isLoading: rtQ.isLoading,
    isError: rtQ.isError,
    hasData: !!rtQ.data,
  });

  let body: JSX.Element;
  if (state === "loading") {
    body = (
      <div className="grid gap-4 lg:grid-cols-5">
        <Skeleton className="h-[260px] lg:col-span-2" />
        <Skeleton className="h-[260px] lg:col-span-3" />
      </div>
    );
  } else if (state === "live") {
    const data = rtQ.data!;
    const recent = data.recent ?? [];
    // Build a tiny rolling spark from the recent activity density (placeholder
    // shape — backend exposes a count + a recent[] window, not a series).
    const spark: Point[] = Array.from({ length: 12 }, (_, i) => ({
      t: String(i),
      v: Math.max(0, Math.round(data.active * (0.7 + 0.3 * Math.sin(i / 1.7)))),
    }));
    body = (
      <div className="grid gap-4 lg:grid-cols-5">
        <Card className="flex flex-col justify-between p-5 lg:col-span-2">
          <div className="flex items-center justify-between">
            <LiveDot label="Live visitors" />
            <AigenTag />
          </div>
          <div className="mt-4">
            <div className="font-semibold leading-none tracking-tight text-accent text-[64px]">
              {data.active}
            </div>
            <p className="mt-2 text-[13px] text-muted">on the site right now</p>
          </div>
          <div className="mt-4">
            <Sparkline data={spark} color="accent" height={40} fill />
            <p className="mt-1 font-mono text-[10px] text-subtle">updating every 5s</p>
          </div>
        </Card>

        <Card className="p-5 lg:col-span-3">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-[14px] font-semibold tracking-tight text-ink-strong">
              Activity stream
            </h3>
            <span className="font-mono text-[11px] text-subtle">updating live</span>
          </div>
          {recent.length === 0 ? (
            <p className="text-[13px] text-subtle">No activity in the last few minutes.</p>
          ) : (
            <ul className="space-y-2.5">
              {recent.map((e, i) => (
                <li
                  key={`${e.ts}-${i}`}
                  className="flex items-center gap-3 border-b border-hairline/60 pb-2.5 last:border-0 last:pb-0"
                >
                  <span className="relative flex h-1.5 w-1.5 shrink-0">
                    <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-accent" />
                  </span>
                  <p className="min-w-0 flex-1 truncate text-[13px] text-ink">
                    Someone <span className="text-muted">{verb(e.type)}</span>{" "}
                    <span className="font-mono text-[12px] text-ink-strong">{e.path || "/"}</span>
                  </p>
                  <span className="shrink-0 font-mono text-[11px] text-subtle">
                    {relativeTime(e.ts)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    );
  } else if (state === "empty") {
    body = (
      <EmptyState
        title="Nobody on the site right now"
        detail="Live visitors and their activity will stream in here as people arrive."
      />
    );
  } else {
    body = <SampleRealtime />;
  }

  return (
    <div className="stagger space-y-6">
      <PanelHeader
        title="Right now"
        desc="A live pulse of who is on the site this minute."
        action={state === "sample" ? <SampleChip /> : undefined}
      />
      {body}
    </div>
  );
}
